import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Download, Search } from "lucide-react";
import { StatusBadge } from "./StatusBadge";
import { useDeviceUpdateRows } from "@/hooks/useSoftwareUpdates";

function toCsv(
  rows: { device: { name: string; serial_number: string | null }; family: string; current: string; latest: string; status: string }[]
): string {
  const header = ["Device", "Serial", "OS", "Current", "Latest", "Status"].join(",");
  const lines = rows.map((r) =>
    [
      r.device.name,
      r.device.serial_number ?? "",
      r.family,
      r.current,
      r.latest,
      r.status,
    ]
      .map((v) => `"${String(v).replace(/"/g, '""')}"`)
      .join(",")
  );
  return [header, ...lines].join("\n");
}

const FAMILY_LABELS: Record<string, string> = {
  ios: "iOS",
  ipados: "iPadOS",
  macos: "macOS",
  tvos: "tvOS",
  watchos: "watchOS",
};

export function DevicesTab() {
  const navigate = useNavigate();
  const { rows, isLoading } = useDeviceUpdateRows();
  const [q, setQ] = useState("");

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return rows;
    return rows.filter(
      (r) =>
        r.device.name.toLowerCase().includes(needle) ||
        (r.device.serial_number ?? "").toLowerCase().includes(needle) ||
        r.family.includes(needle) ||
        r.current.toLowerCase().includes(needle),
    );
  }, [rows, q]);

  const handleExport = () => {
    const csv = toCsv(filtered);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `software-updates-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="relative w-full max-w-sm">
          <Search className="pointer-events-none absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search devices, OS, version…"
            className="pl-8"
            aria-label="Search devices"
          />
        </div>
        <Button variant="outline" size="sm" onClick={handleExport} disabled={!filtered.length}>
          <Download className="mr-1 h-4 w-4" aria-hidden="true" /> Export CSV
        </Button>
      </div>

      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Device</TableHead>
              <TableHead>OS</TableHead>
              <TableHead>Current</TableHead>
              <TableHead>Latest (fleet)</TableHead>
              <TableHead>Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading && (
              <TableRow>
                <TableCell colSpan={5}>
                  <Skeleton className="h-6 w-full" />
                </TableCell>
              </TableRow>
            )}
            {!isLoading && filtered.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} className="text-sm text-muted-foreground">
                  No devices match.
                </TableCell>
              </TableRow>
            )}
            {filtered.map((r) => (
              <TableRow
                key={r.device.id}
                className="cursor-pointer"
                onClick={() => navigate(`/devices/${r.device.id}`)}
              >
                <TableCell className="font-medium">{r.device.name}</TableCell>
                <TableCell>{FAMILY_LABELS[r.family] ?? r.family}</TableCell>
                <TableCell className="tabular-nums">{r.current}</TableCell>
                <TableCell className="tabular-nums">{r.latest}</TableCell>
                <TableCell>
                  <StatusBadge status={r.status} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}

export default DevicesTab;
